"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * The social cards as a row on a wide screen and, Lonely Planet-style, a
 * one-card-at-a-time carousel below 1280px (owner, 26 Sept 2026: no long
 * queue of videos on a phone). Swipe, or use the arrows at the top right;
 * cards snap into place. From 1280px the arrows are hidden and the cards
 * share one centred row, equal widths up to 318px each (four since 30 Sept
 * 2026).
 */
export function SocialCarousel({ children }: { children: ReactNode }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    update();
    track.addEventListener("scroll", update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(track);
    return () => {
      track.removeEventListener("scroll", update);
      resize.disconnect();
    };
  }, [update]);

  const step = (direction: 1 | -1) => {
    const track = trackRef.current;
    const card = track?.firstElementChild as HTMLElement | null;
    if (!track || !card) return;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: "smooth" });
  };

  const arrow =
    "flex h-11 w-11 items-center justify-center border text-lg transition-colors disabled:cursor-default disabled:opacity-35";

  return (
    <div>
      <div className="mb-5 flex justify-end gap-2 xl:hidden">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => step(-1)}
          disabled={atStart}
          className={`${arrow} border-[var(--border-subtle)] bg-transparent text-[var(--foreground)] hover:border-[var(--accent)]`}
        >
          ←
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={() => step(1)}
          disabled={atEnd}
          className={`${arrow} border-[var(--cta)] bg-[var(--cta)] text-[#2b2b2b]`}
        >
          →
        </button>
      </div>
      <div
        ref={trackRef}
        className="-mx-4 flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto scroll-px-4 px-4 pb-2 [scrollbar-width:none] xl:mx-0 xl:grid xl:snap-none xl:auto-cols-[minmax(0,318px)] xl:grid-flow-col xl:justify-center xl:gap-8 xl:overflow-visible xl:px-0 [&::-webkit-scrollbar]:hidden [&>*]:shrink-0 [&>*]:snap-start"
      >
        {children}
      </div>
    </div>
  );
}
